import React, { Fragment } from 'react'
import { Slider, Typography, Grid, Button, TextField, IconButton, FormControl, InputLabel, Select, MenuItem, useTheme, makeStyles, FormControlLabel, Switch, Collapse, Fab } from '@material-ui/core'
import { Alert, Autocomplete } from '@material-ui/lab'
import { NotificationsActive, NotificationsOff, Save, Add, Delete, KeyboardArrowDown } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { throttle } from 'lodash'
import { Trans } from 'react-i18next'
import moment from 'moment'
import { useFetch, mapByIds, useAuth } from '../context/useFetch'
import { useAlert, useUpdate, useChangeHandler, useBatch } from '../reducer'
import i18n from '../i18n/i18n'
import { pictureUrl } from '../common/pictureUrl'
import { byLabel } from '../../pages/job-offer/[pid]/apply'

const useStyles = makeStyles((theme) => ({
  candidateTechnologies: {
    marginBlock: theme.spacing(4),
    '& .item': {
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(0.5),
      '& .technologyId': {
        display: 'grid',
        gridAutoFlow: 'column',
        justifyContent: 'start',
        alignItems: 'center',
        columnGap: theme.spacing(0.5),
        marginBottom: theme.spacing(-1)
      },
      '& .slider': {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        alignItems: 'center',
        columnGap: theme.spacing(2)
      }
    }
  },
  addTechnologies: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    alignItems: 'center',
    columnGap: theme.spacing(2)
  }
}))

export default () => (
  <Fragment>
    <Typography variant="h2" component="h1">
      <Trans i18nKey="profile.candidat.technology_title" />
    </Typography>
    <TechnologyList />
    <Notifications />
  </Fragment>
)

const TechnologyList = () => {
  const candidateTechnologies = useSelector((state) => state.profile.candidates_technologies)
  const update = useUpdate()
  const alert = useAlert()

  const auth = useAuth()

  const handleValueChange = (index) => throttle(
    (_event, value) => {
      update(['profile', 'candidates_technologies', index, 'degree'], value)
    },
    100,
    { trailing: true }
  )

  const toggleNotificationsHandler = (index) => () => {
    update(['profile', 'candidates_technologies', index, 'notifications_enabled'], !candidateTechnologies[index].notifications_enabled)
  }

  const removeCandidateTechnologyHandler = (index) => () => {
    update(['profile', 'candidates_technologies', index, '_destroy'], true)
  }

  const classes = useStyles()

  return useFetch(
    ({ fetch }) => ({
      technos: fetch('technologies').then((results) => mapByIds(results.data)),
      addTechnologies: Promise.resolve({ technologyIds: [] }),
      profile: fetch('profile')
    }),
    ({ technos: technologies }) => {
      if (!technologies) return null

      return (
        <Grid className="profileSection">
          <Typography variant="h3" component="h2">
            <Trans i18nKey="profile.candidat.technology_add" />
          </Typography>

          <Typography>
            <Trans i18nKey="profile.candidat.technology_subtitle" />
          </Typography>

          <div className={classes.candidateTechnologies}>
            {candidateTechnologies
              .sort((a, b) => technologies[a.technology_id].label.localeCompare(technologies[b.technology_id].label))
              .map((candidateTechnology, index) => (
                <div className="item" style={{ opacity: candidateTechnology._destroy ? 0.3 : 1 }}>
                  <div className="technologyId">
                    <img src={pictureUrl({ record_type: 'Technology', record_id: candidateTechnology.technology_id, name: 'icon' }, 112, 112)} alt={(technologies[candidateTechnology.technology_id] || {}).label} width={28} height={28} />
                    <Typography component="h3" variant="h4">{(technologies[candidateTechnology.technology_id] || {}).label}</Typography>
                  </div>
                  <div className="slider">
                    <Slider marks min={0} max={100} step={10} value={candidateTechnology.degree} onChange={handleValueChange(index)} />
                    <div className="iconButton">
                      <IconButton onClick={toggleNotificationsHandler(index)}>
                        {candidateTechnology.notifications_enabled ? <NotificationsActive color="secondary" /> : <NotificationsOff color="disabled" />}
                      </IconButton>
                      <IconButton onClick={removeCandidateTechnologyHandler(index)}>
                        <Delete />
                      </IconButton>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <AddTechnologies />
        </Grid>
      )
    }
  )
}

const AddTechnologies = () => {
  const classes = useStyles()
  const technologies = useSelector(({ technos }) => technos)
  const profile = useSelector(({ profile }) => profile)

  const notUsedTechnologies = { ...technologies }

  profile.candidates_technologies.forEach(({ technology_id }) => {
    delete notUsedTechnologies[technology_id]
  })

  const batch = useBatch()

  return useFetch(
    () => ({}),
    ({ addTechnologies }) => {
      const handler = useChangeHandler(addTechnologies, 'addTechnologies')

      const auth = useAuth()

      const addButtonHandler = () => {
        const technologyIds = [...profile.candidates_technologies.map(({ technology_id }) => technology_id), ...addTechnologies.technologyIds]

        auth
          .fetch('profile', {
            method: 'PATCH',
            body: {
              profile: {
                technology_ids: technologyIds
              }
            }
          })
          .then(() => {
            auth.fetch('profile').then((profile) => {
              batch([], {
                profile,
                addTechnologies: { technologyIds: [] }
              })
            })
          })
      }

      return (
        <Grid className={classes.addTechnologies}>
          <Autocomplete
            multiple
            id="technologies-add"
            options={Object.values(notUsedTechnologies).sort(byLabel)}
            getOptionLabel={(option) => option.label}
            limitTags={10}
            noOptionsText={i18n.t('noOptionsText')}
            renderInput={(params) => (
              <TextField
                id="technologies-select"
                variant="filled"
                label={i18n.t('job_offer.new_job_offer.job.techno')}
                placeholder={i18n.t('job_offer.new_job_offer.job.techno_place_holder')}
                fullWidth
                {...params}
                InputProps={{ ...params.InputProps, disableUnderline: true }}
              />
            )}
            required
            popupIcon={<KeyboardArrowDown />}
            {...handler.multipleComplete('id', technologies)('technologyIds')}
          />

          <Fab color="primary" aria-label={i18n.t('profile.candidat.profile_tab.save')} disabled={addTechnologies.technologyIds.length === 0} onClick={addButtonHandler}>
            <Add />
          </Fab>
        </Grid>
      )
    }
  )
}

const Notifications = () => {
  const theme = useTheme()
  const batch = useBatch()
  const update = useUpdate()
  const auth = useAuth()
  const alert = useAlert()

  const profile = useSelector((state) => state.profile)

  if (!profile) return null

  const options = [
    { label: i18n.t('profile.candidat.notifications.options.1'), value: 1 },
    { label: i18n.t('profile.candidat.notifications.options.2'), value: 3 },
    { label: i18n.t('profile.candidat.notifications.options.3'), value: 6 },
    { label: i18n.t('profile.candidat.notifications.options.4'), value: 12 },
    { label: i18n.t('profile.candidat.notifications.options.5'), value: 'forever' }
  ]

  const saveFrequency = () => {
    const { notifications_disabled_for, user: { receive_newsletter, id } } = profile

    auth.fetch('profile', {
      method: 'PATCH',
      body: {
        profile: {
          notifications_disabled_for: notifications_disabled_for || 'never',
          candidates_technologies_attributes: profile.candidates_technologies,
          user_attributes: { receive_newsletter, id }
        }
      }
    })
      .then(() => {
        alert('notice', <Trans i18nKey="alerts.saved_data" />)
        update(['profile', 'candidates_technologies'], profile.candidates_technologies.filter(({ _destroy }) => !Boolean(_destroy)))
      })
      .catch(() => alert('error', <Trans i18nKey="alerts.saved_error" />))
  }

  const jobAlertSwitchHandler = ({ target: { checked } }) => {
    if (checked) {
      batch(['profile'], {
        notifications_still_disabled: false,
        notifications_enabled: true,
        notifications_disabled_until: null
      })
    } else {
      batch(['profile'], {
        notifications_still_disabled: true,
        notifications_enabled: false,
        notifications_disabled_for: options[0].value
      })
    }
  }

  const selectDisableOptionHandler = ({ target: { value } }) => {
    let newValue = value
    if (newValue === 'forever' && confirm(i18n.t('profile.candidat.notifications.confirm'))) {
      newValue = 6
    }

    batch(['profile'], {
      notifications_still_disabled: true,
      notifications_enabled: newValue !== 'forever',
      notifications_disabled_until: moment().add(newValue, 'months').format('DD MMMM YYYY HH:mm:ss'),
      notifications_disabled_for: newValue
    })
  }

  const newsletterHandler = ({ target: { checked } }) => update(['profile', 'user', 'receive_newsletter'], checked)

  const notificationAlert = profile.notifications_disabled_until ?
    i18n.t('profile.candidat.notifications.partial_deactivation', { date: moment(profile.notifications_disabled_until, 'YYYY-MM-DD').format('DD MMMM YYYY') }) :
    i18n.t('profile.candidat.notifications.total_deactivation')

  return (
    <Fragment>
      <Grid className="profileSection">
        <Typography variant="h3" component="h2">
          {i18n.t('profile.candidat.notifications.title')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch id="newsletter-switch" color="primary" onChange={newsletterHandler} checked={Boolean(profile.user.receive_newsletter)} />}
              label={<Typography component="span" variant="h6">{i18n.t('profile.receive_newsletter.activated')}</Typography>}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch id="email-alert-switch" color="primary" onChange={jobAlertSwitchHandler} checked={Boolean(profile.notifications_enabled && !profile.notifications_still_disabled)} />}
              label={<Typography component="span" variant="h6">{i18n.t('profile.candidat.notifications.activate')}</Typography>}
            />
          </Grid>

          <Grid item xs={12}>
            <Collapse in={!profile.notifications_enabled || profile.notifications_still_disabled}>
              {profile.notifications_disabled_for && (
                <FormControl variant="filled">
                  <InputLabel id="technologies-notifs-label">{i18n.t('profile.candidat.notifications.deactivate')}</InputLabel>
                  <Select labelId="technologies-notifs-label" onChange={selectDisableOptionHandler} value={profile.notifications_disabled_for}>
                    {options.map((option) => (
                      <MenuItem value={option.value}>{option.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!profile.notifications_disabled_for && (
                <Alert severity="warning">
                  {notificationAlert}
                </Alert>
              )}
            </Collapse>
          </Grid>
        </Grid>

      </Grid>

      <Grid className="profileSection" container justifyContent="flex-end">
        <Button variant="contained" size="large" color="primary" startIcon={<Save />} onClick={saveFrequency}>
          <Trans i18nKey="profile.candidat.profile_tab.save" />
        </Button>
      </Grid>
    </Fragment>
  )
}
